import { useState, useMemo } from "react";
import {
  Calendar as CalendarIcon,
  Clock,
  Check,
  ChevronLeft,
  ChevronRight,
  Video,
  Mail,
  User,
  Phone,
  Globe,
  ArrowLeft,
  Loader2,
  MapPin
} from "lucide-react";
import Header from "./header/header";
import Select from "react-select";
import countryList from "react-select-country-list";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

/**
 * Doyenify — Book a Meeting
 *
 * DEMO_MODE = true  -> simulates a successful booking so you can click the whole flow.
 * DEMO_MODE = false -> POSTs to your real endpoint (WebinarFormRouter /schedule-meeting).
 *
 * Your route is behind authMiddleware, so in production pass the Firebase ID token:
 *   headers: { Authorization: `Bearer ${await currentUser.getIdToken()}` }
 */
const DEMO_MODE = false;
const BOOKING_ENDPOINT = "schedule-meeting";
const MEETING_DURATION_MIN = 30;

const PALETTE = {
  green: "#3E8E41",      // brand green — logo, headings, primary actions
  greenDark: "#14532B",  // deep forest — the dark banner / gradients
  greenPale: "#EAF3DE",  // the pale nav-bar green (unchanged — it already matches)
  greenLight: "#B7D99A", // soft accent — the dot, "today" ring
};

// 09:00 – 17:30, every 30 min
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 9; h <= 17; h++) {
    for (const m of [0, 30]) {
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return slots;
})();

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// const COUNTRIES = [
//   "Nigeria","Ghana","Kenya","South Africa","Egypt","Ethiopia","Tanzania","Uganda","Rwanda",
//   "Cameroon","Ivory Coast","Senegal","Morocco","Algeria","Tunisia","Zambia","Zimbabwe","Botswana",
//   "United States","United Kingdom","Canada","Ireland","Germany","France","Netherlands","Belgium",
//   "Spain","Portugal","Italy","Switzerland","Austria","Sweden","Norway","Denmark","Finland","Poland",
//   "Czech Republic","Greece","Romania","Ukraine","Russia","Turkey","United Arab Emirates","Saudi Arabia",
//   "Qatar","Kuwait","Israel","India","Pakistan","Bangladesh","China","Japan","South Korea","Singapore",
//   "Malaysia","Indonesia","Philippines","Thailand","Vietnam","Australia","New Zealand","Brazil","Argentina",
//   "Mexico","Chile","Colombia","Peru","Other",
// ];

function toISODate(d:any) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function humanDate(d:any) {
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function to12h(t:any) {
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${period}`;
}

export default function BookMeeting() {
  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/London",
    []
  );

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [viewMonth, setViewMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const [selectedTime, setSelectedTime] = useState<any>(null);

  const [form, setForm] = useState<any>({ name: "", email: "", phone: "", country: "", topic: "" });
  const [errors, setErrors] = useState<any>({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<any>(null); // { joinUrl, ... }

  
  const options = useMemo(() => countryList().getData(), []); // [{ value: "NG", label: "Nigeria" }, ...]


  // ---- calendar grid ----
  const grid = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) cells.push(new Date(year, month, day));
    return cells;
  }, [viewMonth]);

  const canGoPrev = viewMonth > new Date(today.getFullYear(), today.getMonth(), 1);

  function shiftMonth(delta:any) {
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  }

  function pickDate(d:any) {
    setSelectedDate(d);
    setSelectedTime(null);
  }

  // ---- validation ----
  function validate() {
    const e:any = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    if (!form.country) e.country = "Please select your country.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "That email doesn't look right.";
    if (form.phone && form.phone.replace(/[^\d]/g, "").length < 7) e.phone = "That phone number looks too short.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit() {
    if (!selectedDate || !selectedTime) return;
    if (!validate()) return;

    setSubmitting(true);
    // const payload = {
    //   name: form.name.trim(),
    //   email: form.email.trim(),
    //   phone: form.phone.trim() || undefined,
    //   topic: form.topic.trim() || undefined,
    //   date: toISODate(selectedDate),
    //   time: selectedTime,
    //   timezone,
    // };

    const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        country: form.country,
        topic: form.topic.trim() || undefined,
        date: toISODate(selectedDate),
        time: selectedTime,
        timezone,
    };
    
    try {
      if (DEMO_MODE) {
        await new Promise((r) => setTimeout(r, 1100));
        setConfirmation({
          joinUrl: "https://us05web.zoom.us/j/000000000?pwd=demoDEMOdemo",
          bookingId: "demo-" + Math.random().toString(36).slice(2, 8),
        });
      } else {
         const response = await fetch("https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=" +
            process.env.REACT_APP_SCHEDULE_MEETING_API_KEY, {
            method: "post",
            body: JSON.stringify({
              email: process.env.REACT_APP_SCHEDULE_MEETING_EMAIL,
              password: process.env.REACT_APP_SCHEDULE_MEETING_PASSWORD,
              returnSecureToken: true,
            }),
            headers: {
              "Content-Type": "application/json",
            },
          });
  
          const userToken = await response.json();

        const res = await fetch(`${process.env.REACT_APP_SCHEDULE_MEETING_BASE_URL}/${BOOKING_ENDPOINT}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken.idToken}`,
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Request failed");
        const data = await res.json();
        setConfirmation(data);
      }
    } catch (err) {
      setErrors({ submit: "We couldn't book your meeting. Please try again in a moment." });
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setConfirmation(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setForm({ name: "", email: "", phone: "", country: "", topic: "" });
    setErrors({});
  }

  const readyForDetails = selectedDate && selectedTime;

  return (
    <div>
        <Header />
        <div className="dyn-root pt-[7%]">
      <style>{css}</style>

        
      <div className="dyn-card">
        {/* ---------- LEFT PANEL ---------- */}
        <aside className="dyn-side">
          <div className="dyn-brand">
            <span className="dyn-logo-dot" />
            Doyenify
          </div>

          <div className="dyn-side-body">
            <h1 className="dyn-side-title">Book a session</h1>
            <p className="dyn-side-sub">
              Pick a time that works for you. We'll send a Zoom link to your inbox the moment it's booked.
            </p>

            <ul className="dyn-meta">
              <li><Clock size={16} /> {MEETING_DURATION_MIN} minutes</li>
              <li><Video size={16} /> Zoom video call</li>
              {/* <li><Globe size={16} /> {timezone.replace(/_/g, " ")}</li> */}
              <li><Globe size={16}/> Times shown in West Africa Time (WAT)</li>
            </ul>

            {readyForDetails && !confirmation && (
              <div className="dyn-summary">
                <p className="dyn-summary-label">Your selection</p>
                <p className="dyn-summary-date">{humanDate(selectedDate)}</p>
                <p className="dyn-summary-time">{to12h(selectedTime)}</p>
              </div>
            )}
          </div>

          <p className="dyn-side-foot">Powered by Doyenify</p>
        </aside>

        {/* ---------- RIGHT PANEL ---------- */}
        <main className="dyn-main">
          {confirmation ? (
            <div className="dyn-done">
              <div className="dyn-check"><Check size={30} strokeWidth={3} /></div>
              <h2 className="dyn-done-title">You're booked</h2>
              <p className="dyn-done-sub">
                A confirmation is on its way to <strong>{form.email}</strong>.
              </p>

              <div className="dyn-done-card">
                <div className="dyn-done-row"><CalendarIcon size={17} /><span>{humanDate(selectedDate)}</span></div>
                <div className="dyn-done-row"><Clock size={17} /><span>{to12h(selectedTime)} · {timezone.replace(/_/g, " ")}</span></div>
              </div>

              <a className="dyn-btn dyn-btn-primary dyn-btn-block" href={confirmation.joinUrl} target="_blank" rel="noreferrer">
                <Video size={18} /> Join the meeting
              </a>
              <button className="dyn-link" onClick={reset}>
                <ArrowLeft size={15} /> Book another
              </button>
            </div>
          ) : (
            <>
              {/* --- date & time --- */}
              <section>
                <div className="dyn-step-head">
                  <span className="dyn-step-num">1</span>
                  <h2 className="dyn-step-title">Select a date & time</h2>
                </div>

                <div className="dyn-cal-head">
                  <button
                    className="dyn-cal-nav"
                    onClick={() => shiftMonth(-1)}
                    disabled={!canGoPrev}
                    aria-label="Previous month"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span className="dyn-cal-month">
                    {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
                  </span>
                  <button className="dyn-cal-nav" onClick={() => shiftMonth(1)} aria-label="Next month">
                    <ChevronRight size={18} />
                  </button>
                </div>

                <div className="dyn-cal-grid dyn-cal-labels">
                  {WEEKDAYS.map((w) => <span key={w} className="dyn-cal-wd">{w}</span>)}
                </div>

                <div className="dyn-cal-grid">
                  {grid.map((d, i) => {
                    if (!d) return <span key={i} />;
                    const past = d < today;
                    const active = selectedDate && toISODate(d) === toISODate(selectedDate);
                    const isToday = toISODate(d) === toISODate(today);
                    return (
                      <button
                        key={i}
                        className={`dyn-day${active ? " is-active" : ""}${isToday ? " is-today" : ""}`}
                        disabled={past}
                        onClick={() => pickDate(d)}
                      >
                        {d.getDate()}
                      </button>
                    );
                  })}
                </div>

                {selectedDate && (
                  <div className="dyn-slots">
                    {TIME_SLOTS.map((t) => (
                      <button
                        key={t}
                        className={`dyn-slot${selectedTime === t ? " is-active" : ""}`}
                        onClick={() => setSelectedTime(t)}
                      >
                        {to12h(t)}
                      </button>
                    ))}
                  </div>
                )}
              </section>

              {/* --- details form --- */}
              {readyForDetails && (
                <section className="dyn-form-wrap">
                  <div className="dyn-step-head">
                    <span className="dyn-step-num">2</span>
                    <h2 className="dyn-step-title">Your details</h2>
                  </div>

                  <div className="dyn-field">
                    <label className="dyn-label">Full name</label>
                    <div className={`dyn-input-wrap${errors.name ? " has-error" : ""}`}>
                      <User size={17} className="dyn-input-icon" />
                      <input
                        className="dyn-input"
                        placeholder="Ada Lovelace"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                      />
                    </div>
                    {errors.name && <span className="dyn-err">{errors.name}</span>}
                  </div>

                  <div className="dyn-field">
                    <label className="dyn-label">Email</label>
                    <div className={`dyn-input-wrap${errors.email ? " has-error" : ""}`}>
                      <Mail size={17} className="dyn-input-icon" />
                      <input
                        className="dyn-input"
                        placeholder="you@example.com"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                      />
                    </div>
                    {errors.email && <span className="dyn-err">{errors.email}</span>}
                  </div>

                  <div className="dyn-field">
                    <label className="dyn-label">Phone <span className="dyn-optional">optional</span></label>
                    <div className={`dyn-input-wrap${errors.phone ? " has-error" : ""}`}>
                      <Phone size={17} className="dyn-input-icon" />
                     
                      <PhoneInput
                          international
                          defaultCountry="NG"
                          value={form.phone}
                          onChange={(value) => setForm({ ...form, phone: value ?? "" })}
                          placeholder="+234 801 234 5678"
                        />
                    </div>
                    {errors.phone && <span className="dyn-err">{errors.phone}</span>}
                  </div>

                  <div className="dyn-field">
                    <label className="dyn-label">Country</label>
                    <div className={`dyn-input-wrap${errors.country ? " has-error" : ""}`}>
                        <MapPin size={17} className="dyn-input-icon" />
                      
                        <Select
                            options={options}
                            value={options.find((o) => o.label === form.country) || null}
                            onChange={(opt) => setForm({ ...form, country: opt?.label ?? "" })}
                            placeholder="Select your country"
                            classNamePrefix="dyn-rs"
                            isSearchable
                            menuPlacement="auto"
                            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                          />
                    </div>
                    {errors.country && <span className="dyn-err">{errors.country}</span>}
                    </div>

                  <div className="dyn-field">
                    <label className="dyn-label">What's it about? <span className="dyn-optional">optional</span></label>
                    <textarea
                      className="dyn-textarea"
                      placeholder="A short note on what you'd like to cover…"
                      rows={3}
                      value={form.topic}
                      onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    />
                  </div>

                  {errors.submit && <div className="dyn-submit-err">{errors.submit}</div>}

                  <button
                    className="dyn-btn dyn-btn-primary dyn-btn-block"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? (<><Loader2 size={18} className="dyn-spin" /> Booking…</>) : "Confirm booking"}
                  </button>
                </section>
              )}
            </>
          )}
        </main>
      </div>
    </div>
    </div>
    
  );
}

const css = `
.dyn-root{
  --green:${PALETTE.green};--green-dark:${PALETTE.greenDark};
  --green-pale:${PALETTE.greenPale};--green-light:${PALETTE.greenLight};
  --ink:#1a201d;--muted:#6b7280;--line:#e6e8e6;--bg:#f4f6f4;
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  color:var(--ink);background:var(--bg);min-height:100vh;
  display:flex;align-items:center;justify-content:center;padding:32px 16px;box-sizing:border-box;
}
.dyn-root *{box-sizing:border-box;}
.dyn-card{
  width:100%;max-width:940px;background:#fff;border-radius:20px;overflow:hidden;
  box-shadow:0 20px 50px -20px rgba(20,83,43,.28),0 2px 8px rgba(0,0,0,.04);
  display:grid;grid-template-columns:340px 1fr;
}

/* left panel */
.dyn-side{
  background:linear-gradient(160deg,var(--green) 0%,var(--green-dark) 100%);
  color:#fff;padding:32px 30px;display:flex;flex-direction:column;position:relative;
}
.dyn-brand{display:flex;align-items:center;gap:9px;font-weight:700;font-size:19px;letter-spacing:-.01em;}
.dyn-logo-dot{width:11px;height:11px;border-radius:50%;background:var(--green-light);box-shadow:0 0 0 4px rgba(192,221,151,.25);}
.dyn-side-body{margin-top:38px;flex:1;}
.dyn-side-title{font-size:28px;line-height:1.15;font-weight:700;margin:0 0 12px;letter-spacing:-.02em;}
.dyn-side-sub{font-size:14px;line-height:1.6;color:rgba(255,255,255,.82);margin:0 0 26px;}
.dyn-meta{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:13px;}
.dyn-meta li{display:flex;align-items:center;gap:10px;font-size:14px;color:rgba(255,255,255,.92);}
.dyn-meta svg{color:var(--green-light);flex-shrink:0;}
.dyn-summary{margin-top:28px;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.18);border-radius:14px;padding:16px 18px;}
.dyn-summary-label{margin:0 0 8px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.09em;color:var(--green-light);}
.dyn-summary-date{margin:0;font-size:15px;font-weight:600;}
.dyn-summary-time{margin:4px 0 0;font-size:14px;color:rgba(255,255,255,.85);}
.dyn-side-foot{margin:24px 0 0;font-size:12px;color:rgba(255,255,255,.6);}

/* right panel */
.dyn-main{padding:34px 36px;min-width:0;}
.dyn-step-head{display:flex;align-items:center;gap:11px;margin-bottom:20px;}
.dyn-step-num{
  width:26px;height:26px;border-radius:50%;background:var(--green-pale);color:var(--green-dark);
  font-size:13px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;
}
.dyn-step-title{font-size:17px;font-weight:600;margin:0;letter-spacing:-.01em;}

/* calendar */
.dyn-cal-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;}
.dyn-cal-month{font-size:15px;font-weight:600;}
.dyn-cal-nav{
  width:34px;height:34px;border-radius:9px;border:1px solid var(--line);background:#fff;color:var(--ink);
  display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .15s;
}
.dyn-cal-nav:hover:not(:disabled){background:var(--green-pale);border-color:var(--green-light);color:var(--green-dark);}
.dyn-cal-nav:disabled{opacity:.35;cursor:not-allowed;}
.dyn-cal-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:6px;}
.dyn-cal-labels{margin-bottom:8px;}
.dyn-cal-wd{text-align:center;font-size:11px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:.04em;padding:4px 0;}
.dyn-day{
  aspect-ratio:1;border:1px solid transparent;border-radius:10px;background:#fff;color:var(--ink);
  font-size:14px;font-weight:500;cursor:pointer;transition:all .14s;font-family:inherit;
}
.dyn-day:hover:not(:disabled){background:var(--green-pale);color:var(--green-dark);}
.dyn-day:disabled{color:#cbd0cb;cursor:not-allowed;}
.dyn-day.is-today{border-color:var(--green-light);font-weight:700;}
.dyn-day.is-active{background:var(--green);color:#fff;border-color:var(--green);box-shadow:0 4px 12px -3px rgba(62,142,65,.55);}
.dyn-day.is-active:hover{background:var(--green);color:#fff;}

/* time slots */
.dyn-slots{
  display:grid;grid-template-columns:repeat(auto-fill,minmax(88px,1fr));gap:9px;
  margin-top:22px;padding-top:22px;border-top:1px solid var(--line);
  max-height:196px;overflow-y:auto;
}
.dyn-slot{
  padding:10px 8px;border:1px solid var(--line);border-radius:10px;background:#fff;color:var(--ink);
  font-size:13.5px;font-weight:600;cursor:pointer;transition:all .14s;font-family:inherit;
}
.dyn-slot:hover{border-color:var(--green);color:var(--green-dark);background:var(--green-pale);}
.dyn-slot.is-active{background:var(--green);color:#fff;border-color:var(--green);}

/* form */
.dyn-form-wrap{margin-top:30px;padding-top:30px;border-top:1px solid var(--line);animation:dyn-rise .32s ease;}
.dyn-field{margin-bottom:16px;}
.dyn-label{display:block;font-size:13px;font-weight:600;margin-bottom:7px;color:var(--ink);}
.dyn-optional{font-weight:400;color:var(--muted);font-size:12px;}
.dyn-input-wrap{display:flex;align-items:center;gap:9px;border:1px solid var(--line);border-radius:11px;padding:0 13px;background:#fff;transition:all .15s;}
.dyn-input-wrap:focus-within{border-color:var(--green);box-shadow:0 0 0 3px rgba(62,142,65,.14);}
.dyn-input-wrap.has-error{border-color:#dc2626;}
.dyn-input-icon{color:var(--muted);flex-shrink:0;}
.dyn-input{flex:1;border:none;outline:none;padding:12px 0;font-size:14.5px;background:transparent;color:var(--ink);font-family:inherit;}
.dyn-select{flex:1;border:none;outline:none;padding:12px 0;font-size:14.5px;background:transparent;color:var(--ink);font-family:inherit;cursor:pointer;}
.dyn-select.is-placeholder{color:var(--muted);}
.dyn-textarea{width:100%;border:1px solid var(--line);border-radius:11px;padding:12px 13px;font-size:14.5px;color:var(--ink);font-family:inherit;resize:vertical;outline:none;transition:all .15s;}
.dyn-textarea:focus{border-color:var(--green);box-shadow:0 0 0 3px rgba(62,142,65,.14);}
.dyn-err{display:block;color:#dc2626;font-size:12.5px;margin-top:6px;}
.dyn-submit-err{background:#fef2f2;border:1px solid #fecaca;color:#b91c1c;font-size:13px;padding:11px 14px;border-radius:10px;margin-bottom:14px;}

/* buttons */
.dyn-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;border:none;cursor:pointer;font-family:inherit;font-weight:600;transition:all .16s;text-decoration:none;}
.dyn-btn-primary{background:var(--green);color:#fff;font-size:15px;padding:14px 24px;border-radius:12px;box-shadow:0 6px 16px -6px rgba(62,142,65,.6);}
.dyn-btn-primary:hover:not(:disabled){background:var(--green-dark);}
.dyn-btn-primary:disabled{opacity:.7;cursor:default;}
.dyn-btn-block{width:100%;margin-top:8px;}

/* confirmation */
.dyn-done{display:flex;flex-direction:column;align-items:center;text-align:center;padding:14px 4px;animation:dyn-rise .35s ease;}
.dyn-check{width:64px;height:64px;border-radius:50%;background:var(--green-pale);color:var(--green);display:flex;align-items:center;justify-content:center;margin-bottom:20px;}
.dyn-done-title{font-size:23px;font-weight:700;margin:0 0 8px;letter-spacing:-.02em;}
.dyn-done-sub{font-size:14.5px;color:var(--muted);margin:0 0 24px;line-height:1.5;}
.dyn-done-card{width:100%;background:var(--green-pale);border-radius:14px;padding:18px 20px;margin-bottom:22px;display:flex;flex-direction:column;gap:12px;}
.dyn-done-row{display:flex;align-items:center;gap:11px;font-size:14.5px;font-weight:500;color:var(--green-dark);}
.dyn-done-row svg{color:var(--green);flex-shrink:0;}
.dyn-link{display:inline-flex;align-items:center;gap:6px;background:none;border:none;color:var(--muted);font-size:13.5px;font-weight:500;cursor:pointer;margin-top:14px;font-family:inherit;padding:6px;}
.dyn-link:hover{color:var(--green-dark);}

/* ---- react-phone-number-input: blend into dyn-input-wrap ---- */
.dyn-input-wrap .PhoneInput{
  flex:1;display:flex;align-items:center;gap:8px;min-width:0;
}
.dyn-input-wrap .PhoneInputInput{
  flex:1;min-width:0;border:none;outline:none;background:transparent;
  padding:12px 0;font-size:14.5px;font-family:inherit;color:var(--ink);
}
.dyn-input-wrap .PhoneInputCountry{
  margin:0;gap:5px;
}
.dyn-input-wrap .PhoneInputCountryIcon{
  box-shadow:none;border-radius:2px;overflow:hidden;
}
.dyn-input-wrap .PhoneInputCountrySelectArrow{
  color:var(--muted);opacity:.65;
}

/* ---- react-select: blend into dyn-input-wrap ---- */
.dyn-input-wrap .dyn-rs__control{
  flex:1;border:none;background:transparent;box-shadow:none;
  min-height:auto;cursor:pointer;
}
.dyn-input-wrap .dyn-rs__control--is-focused{border:none;box-shadow:none;}
.dyn-input-wrap .dyn-rs__value-container{padding:12px 0; width: 200px}
.dyn-input-wrap .dyn-rs__input-container,
.dyn-input-wrap .dyn-rs__single-value{
  color:var(--ink);font-size:14.5px;margin:0;
}
.dyn-input-wrap .dyn-rs__placeholder{color:var(--muted);font-size:14.5px;}
.dyn-input-wrap .dyn-rs__indicator-separator{display:none;}
.dyn-input-wrap .dyn-rs__dropdown-indicator{padding:0;color:var(--muted);}
.dyn-input-wrap .dyn-rs__dropdown-indicator:hover{color:var(--green-dark);}

/* menu */
/* menu — portaled to body, so NOT prefixed with .dyn-input-wrap */
.dyn-rs__menu-portal{z-index:9999;}
.dyn-rs__menu{
  width:100% !important;
  border-radius:11px !important;border:1px solid var(--line) !important;
  box-shadow:0 12px 30px -12px rgba(20,83,43,.28) !important;
  overflow:hidden;
}
.dyn-rs__menu-list{max-height:240px;}
.dyn-rs__option{font-size:14px;cursor:pointer;padding:9px 12px;}
.dyn-rs__option--is-focused{background:var(--green-pale) !important;color:var(--green-dark) !important;}
.dyn-rs__option--is-selected{background:var(--green) !important;color:#fff !important;}
.dyn-spin{animation:dyn-spin 1s linear infinite;}
@keyframes dyn-spin{to{transform:rotate(360deg);}}
@keyframes dyn-rise{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}

/* responsive */
@media (max-width:760px){
  .dyn-card{grid-template-columns:1fr;max-width:460px;}
  .dyn-side{padding:26px 26px 28px;}
  .dyn-side-body{margin-top:24px;}
  .dyn-side-title{font-size:24px;}
  .dyn-main{padding:28px 24px;}
}
@media (prefers-reduced-motion:reduce){
  .dyn-form-wrap,.dyn-done{animation:none;}
  *{transition:none !important;}
}
`;
